import React from 'react';
import styles from "../../LocationForm.module.css";
import {
    LocationFormSelectionProvider,
    LocationRequest,
    LocationRequestPartial,
    WorkingHoursRequest
} from "../../LocationForm";
import {ValidationResult} from "../../../../../../elements/validator/component/ValidationComponent";
import {useAppSelector} from "../../../../../../../app/hooks";
import {Section} from "../../../../../../elements/section/Section";
import {ValidatedInputField} from "../../../../../../elements/validator/component/ValidatedInputField";
import {
    MultipleEmailNotEmptyValidator,
    OptionalUrlValidator,
    UrlValidator
} from "../../../../../../elements/validator/Validator";
import {
    CustomSwitchFieldWithTitle
} from "../../../../../../elements/customSwitchFieldWithTitle/CustomSwitchFieldWithTitle";
import {WorkSchedule} from "../workSchedule/WorkSchedule";
import {ValidatedChipInputField} from "../../../../../../elements/validator/component/ValidatedChipInputField";
import {SupportedManufacturersField} from "./SupportedManufacturersField";

interface Props<T, W> {
    getMessage: (s: any) => any;
    updateRequest: (update: LocationRequestPartial) => void;
    selectionProvider: LocationFormSelectionProvider<W>;
    updateValidationStatus: (status: ValidationResult<T>) => void;
    updateWorkingHours: (update: WorkingHoursRequest & { fieldName: string }) => void;
}

export const AftersalesInformation = <
    T extends LocationRequest,
    W extends WorkingHoursRequest
>({
      getMessage,
      updateRequest,
      selectionProvider,
      updateWorkingHours,
      updateValidationStatus,
  }: Props<T, W>) => {

    const aftersalesEnabled = useAppSelector(selectionProvider.aftersalesEnabled)

    return <Section title={"Aftersales"}>
        <CustomSwitchFieldWithTitle
            textOnTrue={"On"}
            textOnFalse={"Off"}
            title={"Aftersales"}
            description={"Enable Aftersales"}
            valueSelector={selectionProvider.aftersalesEnabled}
            toggleValue={(v) => updateRequest({aftersalesEnabled: v})}
        />
        {aftersalesEnabled && <>
            <div className={`${styles.fieldsLineWithChipSelector} ${styles.fieldsLineWithMargin}`}>
                <ValidatedChipInputField<T>
                    onChange={(v) => updateRequest({aftersalesEmailTranscriptRecipients: v})}
                    updateValidationStatus={updateValidationStatus}
                    fieldName={'aftersalesEmailTranscriptRecipients'}
                    selector={selectionProvider.aftersalesEmailTranscriptRecipients}
                    validator={MultipleEmailNotEmptyValidator}
                    label={'Transcript Recipient(s)'}
                    placeholder={"example@mail.com"}
                    getMessage={getMessage}
                />
            </div>
            <CustomSwitchFieldWithTitle
                textOnTrue={"On"}
                textOnFalse={"Off"}
                title={"Transcripts Filtration"}
                description={"Only chats with a stored user email or phone number will be sent to you"}
                valueSelector={selectionProvider.aftersalesEmailFiltrationEnabled}
                toggleValue={(v) => updateRequest({aftersalesEmailFiltrationEnabled: v})}
            />
            <CustomSwitchFieldWithTitle
                textOnTrue={"On"}
                textOnFalse={"Off"}
                title={"Transcript Summary"}
                description={"Generate summary for aftersales transcripts when trigger tools are used"}
                valueSelector={selectionProvider.aftersalesEmailTranscriptSummaryEnabled}
                toggleValue={(v) => updateRequest({aftersalesEmailTranscriptSummaryEnabled: v})}
            />
            <SupportedManufacturersField
                updateRequest={updateRequest}
                supportedVehicleManufacturersSelector={selectionProvider.supportedVehicleManufacturers}
            />
            <div className={styles.fieldsLineWithMargin}>
                <ValidatedInputField<string, T>
                    required={aftersalesEnabled}
                    onChange={(v) => updateRequest({serviceBookingUrl: v})}
                    updateValidationStatus={updateValidationStatus}
                    fieldName={'serviceBookingUrl'}
                    selector={selectionProvider.serviceBookingUrl}
                    validator={UrlValidator}
                    label={'Service Appointment URL'}
                    getMessage={getMessage}
                />
            </div>
            <div className={styles.fieldsLineWithMargin}>
                <ValidatedInputField<string, T>
                    required={false}
                    onChange={(v) => updateRequest({recallUrl: v})}
                    updateValidationStatus={updateValidationStatus}
                    fieldName={'recallUrl'}
                    selector={selectionProvider.recallUrl}
                    validator={OptionalUrlValidator}
                    label={'Recall URL'}
                    getMessage={getMessage}
                />
            </div>
            <WorkSchedule<W>
                title={"Aftersales Availability"}
                backgroundColor={"#f9fbfc"}
                updateWorkingHours={updateWorkingHours}
                mondayWorkingHoursSelector={selectionProvider.mondayServiceWorkingHoursRequest}
                tuesdayWorkingHoursSelector={selectionProvider.tuesdayServiceWorkingHoursRequest}
                wednesdayWorkingHoursSelector={selectionProvider.wednesdayServiceWorkingHoursRequest}
                thursdayWorkingHoursSelector={selectionProvider.thursdayServiceWorkingHoursRequest}
                fridayWorkingHoursSelector={selectionProvider.fridayServiceWorkingHoursRequest}
                saturdayWorkingHoursSelector={selectionProvider.saturdayServiceWorkingHoursRequest}
                sundayWorkingHoursSelector={selectionProvider.sundayServiceWorkingHoursRequest}
            />
        </>}
    </Section>
}
